/**
 * Defining a competency, and editing a draft.
 *
 * Who it applies to is roles and departments; empty means everyone. The
 * evidence requirements are three switches, and the attestation wording
 * is the competency's own if it wants one.
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { COMP_RENEWAL_DAYS_MAX, COMP_TEXT_MAX, COMP_TITLE_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { useCompetencyDetail, useCreateCompetency, useUpdateCompetency } from './useCompetencies';
import {
  COMPETENCY_TYPE_LABEL, COMPETENCY_TYPES, DEFAULT_ATTESTATION, EMPTY_FIELDS, VALIDATION_METHOD_LABEL, VALIDATION_METHODS,
  type CompetencyDetail, type CompetencyFields,
} from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Competencies.module.css';

export const NOT_DRAFT_TITLE = 'Only a draft can be edited';

function fieldsFrom(c: CompetencyDetail): CompetencyFields {
  return {
    title: c.title, description: c.description ?? '',
    appliesToRoles: c.appliesToRoles, appliesToDepartments: c.appliesToDepartments,
    attestation: c.evidenceRequirements.attestation, attestationText: c.evidenceRequirements.attestationText ?? '',
    fileUpload: c.evidenceRequirements.fileUpload, managerApproval: c.evidenceRequirements.managerApproval,
    renewalIntervalDays: c.renewalIntervalDays ? String(c.renewalIntervalDays) : '',
    competencyType: c.competencyType, validationMethods: c.validationMethods,
  };
}

export function CompetencyEditPage() {
  const { competencyId } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const editing = Boolean(competencyId);
  const detail = useCompetencyDetail(orgId, competencyId ?? null, editing);

  if (!editing) return <CompetencyForm orgId={orgId} />;
  if (detail.isLoading) return <Spinner label="Opening the competency" />;
  if (detail.error || !detail.data) {
    return (
      <div className={styles.body}>
        <PageState title="Could not open the competency" action={{ to: '/competencies', label: 'Back to competencies' }} tone="alert">
          {isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }
  if (detail.data.competency.status !== 'draft') {
    return (
      <div className={styles.body}>
        <PageState title={NOT_DRAFT_TITLE} action={{ to: `/competencies/${competencyId}`, label: 'Back to the competency' }}>
          A published competency is what people were assigned; changing it underneath them would change the record.
          To change what is required, publish a new version from its page.
        </PageState>
      </div>
    );
  }
  return <CompetencyForm orgId={orgId} record={detail.data.competency} />;
}

function CompetencyForm({ orgId, record }: { orgId: string | null; record?: CompetencyDetail }) {
  const navigate = useNavigate();
  const create = useCreateCompetency(orgId);
  const update = useUpdateCompetency(orgId, record?.id ?? '');
  const departments = useTaxonomy('department', Boolean(orgId));
  const [f, setF] = useState<CompetencyFields>(() => (record ? fieldsFrom(record) : EMPTY_FIELDS));
  const [touched, setTouched] = useState(false);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;
  const set = <K extends keyof CompetencyFields>(k: K, v: CompetencyFields[K]) => setF((c) => ({ ...c, [k]: v }));

  const titleMissing = touched && !f.title.trim();
  const noEvidence = touched && !f.attestation && !f.fileUpload && !f.managerApproval;
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const backTo = record ? `/competencies/${record.id}` : '/competencies';

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!f.title.trim() || (!f.attestation && !f.fileUpload && !f.managerApproval)) return;
    if (record) update.mutate(f, { onSuccess: () => navigate(backTo) });
    else create.mutate(f, { onSuccess: (newId) => navigate(`/competencies/${newId}`) });
  }
  const toggle = (k: 'appliesToRoles' | 'appliesToDepartments', v: string) =>
    set(k, f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]);
  const toggleMethod = (m: (typeof VALIDATION_METHODS)[number]) =>
    set('validationMethods', f.validationMethods.includes(m)
      ? f.validationMethods.filter((x) => x !== m)
      : VALIDATION_METHODS.filter((x) => x === m || f.validationMethods.includes(x)));

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          {record && <p className={styles.eyebrow}>Competency · Draft</p>}
          <div className={styles.titleLine}>
            <BackLink to={backTo} label={record ? 'Back to the competency' : 'Back to competencies'} />
            <h1 className={styles.title}>{record ? `Edit ${record.title}` : 'New competency'}</h1>
          </div>
          <p className={styles.subtitle}>{record ? 'Changes are saved to this draft.' : 'Define what staff must be able to do and how they prove it. It starts as a draft; publishing assigns it.'}</p>
        </div>
      </header>

      <div className={styles.body}>
        <form className={form.form} onSubmit={submit} noValidate style={{ marginTop: 0, maxWidth: 720 }}>
          {error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{isCallFailure(error) ? error.message : 'The competency could not be saved.'}</div>}

          <div className={form.field}>
            <label className={form.label} htmlFor={id('title')}>Title</label>
            <input id={id('title')} className={form.input} type="text" required maxLength={COMP_TITLE_MAX} value={f.title}
              onChange={(e) => set('title', e.target.value.slice(0, COMP_TITLE_MAX))} aria-invalid={titleMissing || undefined}
              aria-describedby={titleMissing ? id('title-err') : undefined} autoComplete="off" />
            <CharCount value={f.title} max={COMP_TITLE_MAX} />
            {titleMissing && <p className={form.error} id={id('title-err')}>Give the competency a title.</p>}
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('desc')}>Description <span className={form.optional}>(optional)</span></label>
            <textarea id={id('desc')} className={form.textarea} rows={4} maxLength={COMP_TEXT_MAX} value={f.description}
              onChange={(e) => set('description', e.target.value.slice(0, COMP_TEXT_MAX))} aria-describedby={id('desc-hint')} />
            <CharCount value={f.description} max={COMP_TEXT_MAX} />
            <p className={form.hint} id={id('desc-hint')}>What the person must be able to do, and to what standard.</p>
          </div>

          <SegmentedChoice legend="Type" name={id('ctype')} value={f.competencyType} onChange={(v) => set('competencyType', v)} stacked
            options={COMPETENCY_TYPES.map((t) => ({ value: t, label: COMPETENCY_TYPE_LABEL[t] }))} describedBy={id('ctype-hint')} />
          <p className={form.hint} id={id('ctype-hint')} style={{ marginTop: 'calc(var(--sp-4) * -1)', marginBottom: 'var(--sp-6)' }}>
            Initial is the orientation set; annual and ongoing are the ones a surveyor samples; unit-specific and equipment follow a transfer or a new device.
          </p>

          <fieldset className={form.field} style={{ border: 0, padding: 0 }} aria-describedby={id('vm-hint')}>
            <legend className={form.label}>How it is validated <span className={form.optional}>(optional)</span></legend>
            <ul className={own.checks}>
              {VALIDATION_METHODS.map((m) => (
                <li key={m}><label className={own.check}><input type="checkbox" checked={f.validationMethods.includes(m)} onChange={() => toggleMethod(m)} />{VALIDATION_METHOD_LABEL[m]}</label></li>
              ))}
            </ul>
            <p className={form.hint} id={id('vm-hint')}>The method the evidence must show. Surveyors ask for it by name.</p>
          </fieldset>

          <h2 className={form.group}>Who it applies to</h2>
          <p className={`${form.hint} ${form.groupHint}`}>Leave both empty and it applies to everyone. Choose roles, departments, or both — a person matches if they are in any of them.</p>
          <fieldset className={form.field} style={{ border: 0, padding: 0 }}>
            <legend className={form.label}>Roles</legend>
            <ul className={own.checks}>
              {ASSIGNABLE_ROLES.map((r) => (
                <li key={r}><label className={own.check}><input type="checkbox" checked={f.appliesToRoles.includes(r)} onChange={() => toggle('appliesToRoles', r)} />{ROLE_DISPLAY_NAMES[r]}</label></li>
              ))}
            </ul>
          </fieldset>
          <fieldset className={form.field} style={{ border: 0, padding: 0 }}>
            <legend className={form.label}>Departments</legend>
            {(departments.data ?? []).length === 0 ? <p className={form.hint}>No departments are set up yet.</p> : (
              <ul className={own.checks}>
                {(departments.data ?? []).filter((d) => d.isActive || f.appliesToDepartments.includes(d.id)).map((d) => (
                  <li key={d.id}><label className={own.check}><input type="checkbox" checked={f.appliesToDepartments.includes(d.id)} onChange={() => toggle('appliesToDepartments', d.id)} />{d.name}</label></li>
                ))}
              </ul>
            )}
          </fieldset>

          <h2 className={form.group}>Evidence required</h2>
          <fieldset className={form.field} style={{ border: 0, padding: 0 }} aria-describedby={id('ev-hint')}>
            <legend className="vh">Evidence requirements</legend>
            <label className={own.req}>
              <input type="checkbox" checked={f.attestation} onChange={(e) => set('attestation', e.target.checked)} />
              <span><b>Attestation</b><span>The person confirms a statement. The wording below, or the default if left empty.</span></span>
            </label>
            {f.attestation && (
              <div className={form.field} style={{ margin: 'var(--sp-2) 0 var(--sp-3) 28px' }}>
                <label className={form.label} htmlFor={id('att')}>Attestation wording <span className={form.optional}>(optional)</span></label>
                <textarea id={id('att')} className={form.textarea} rows={2} maxLength={COMP_TEXT_MAX} value={f.attestationText}
                  onChange={(e) => set('attestationText', e.target.value.slice(0, COMP_TEXT_MAX))} placeholder={DEFAULT_ATTESTATION} />
                <CharCount value={f.attestationText} max={COMP_TEXT_MAX} />
              </div>
            )}
            <label className={own.req}>
              <input type="checkbox" checked={f.fileUpload} onChange={(e) => set('fileUpload', e.target.checked)} />
              <span><b>File upload</b><span>A certificate, a completed checklist, a photo of a signed skills sheet.</span></span>
            </label>
            <label className={own.req}>
              <input type="checkbox" checked={f.managerApproval} onChange={(e) => set('managerApproval', e.target.checked)} />
              <span><b>Manager approval</b><span>An administrator reviews the evidence and signs it off before the assignment counts.</span></span>
            </label>
            <p className={noEvidence ? form.error : form.hint} id={id('ev-hint')}>{noEvidence ? 'Require at least one kind of evidence.' : 'At least one is required.'}</p>
          </fieldset>

          <h2 className={form.group}>Renewal</h2>
          <div className={form.field}>
            <label className={form.label} htmlFor={id('renew')}>Renews every <span className={form.optional}>(days, optional)</span></label>
            <input id={id('renew')} className={form.input} type="number" min={1} max={COMP_RENEWAL_DAYS_MAX} step={1} value={f.renewalIntervalDays}
              onChange={(e) => set('renewalIntervalDays', e.target.value)} aria-describedby={id('renew-hint')} style={{ maxWidth: 200 }} />
            <p className={form.hint} id={id('renew-hint')}>Annual competencies are 365. Leave empty for a one-time requirement.</p>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={pending}>{pending ? 'Saving…' : record ? 'Save changes' : 'Create competency'}</button>
            <Link to={backTo} className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}
